/**
 * 平台 API 客户端（无业务线前缀）
 *
 * 用于平台级接口：登录、菜单、角色、用户管理、操作日志等
 * baseURL 固定为 /admin/api，不拼接业务线前缀
 */
import { getAuthToken, clearAuthData, type ApiResponse } from './client'
import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios'

const API_ORIGIN = import.meta.env.VITE_API_ORIGIN || ''
const BASE_URL = `${API_ORIGIN}/admin/api`
const TIMEOUT = 30000

const platformClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
})

platformClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAuthToken()
    if (token && config.headers) {
      config.headers['token-admin'] = token
    }
    return config
  },
  (error) => Promise.reject(error)
)

platformClient.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    const { data } = response
    if (data.code === '10000') {
      return response
    }
    const errorMessage = data.message || '请求失败'
    switch (data.code) {
      case '10009':
        clearAuthData()
        setTimeout(() => { window.location.href = '/login' }, 100)
        break
      case '10008':
        console.error('权限不足，无法访问该资源')
        break
      default:
        console.error(`业务错误 [${data.code}]: ${errorMessage}`)
    }
    return Promise.reject(new Error(errorMessage))
  },
  (error) => {
    let errorMessage = '网络错误，请稍后重试'
    if (error.response) {
      const { status, data } = error.response
      switch (status) {
        case 400: errorMessage = '请求参数错误'; break
        case 401:
          errorMessage = '登录已过期，请重新登录'
          clearAuthData()
          setTimeout(() => { window.location.href = '/login' }, 100)
          break
        case 403: errorMessage = '权限不足，无法访问'; break
        case 404: errorMessage = '请求的资源不存在'; break
        case 500: errorMessage = '服务器内部错误'; break
        case 502: errorMessage = '网关错误'; break
        case 503: errorMessage = '服务暂时不可用'; break
        case 504: errorMessage = '网关超时'; break
        default: errorMessage = data?.message || `请求失败 (${status})`
      }
    } else if (error.request) {
      errorMessage = '网络连接失败，请检查网络'
    } else {
      errorMessage = error.message || '请求配置错误'
    }
    console.error('HTTP 错误:', errorMessage)
    return Promise.reject(new Error(errorMessage))
  }
)

export async function get<T>(
  url: string,
  params?: Record<string, unknown>,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await platformClient.get<ApiResponse<T>>(url, { params, ...config })
  return response.data.data
}

export async function post<T>(
  url: string,
  data?: Record<string, unknown> | unknown,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await platformClient.post<ApiResponse<T>>(url, data, config)
  return response.data.data
}

export async function put<T>(
  url: string,
  data?: Record<string, unknown> | unknown,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await platformClient.put<ApiResponse<T>>(url, data, config)
  return response.data.data
}

export async function del<T>(
  url: string,
  params?: Record<string, unknown>,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await platformClient.delete<ApiResponse<T>>(url, { params, ...config })
  return response.data.data
}

export async function patch<T>(
  url: string,
  data?: Record<string, unknown> | unknown,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await platformClient.patch<ApiResponse<T>>(url, data, config)
  return response.data.data
}

export { platformClient }
export default platformClient
